import React, { useEffect, useState } from "react";
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  MapPin, 
  Edit3, 
  Save, 
  X, 
  Loader2,
  ShieldAlert,
  ShieldUser
} from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";

const ProfilesectionHome = () => {
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Look specifically for the User token
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");

        if (!token) {
          console.error("No User token found.");
          setLoading(false);
          return;
        }

        // Axios instance will automatically attach the token based on your config
        const response = await axiosInstance.get("/users/profile");

        if (response.data.success) {
          setFormData(response.data.user);
          setOriginalData(response.data.user);
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
      } finally {
        // Ensures the spinner disappears no matter what happens
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormData(originalData);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.put("/users/profile", {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        companyAddress: formData.companyAddress,
      });

      if (response.data.success) {
        setOriginalData(formData);
        setIsEditing(false);
        // Refresh to sync changes with Sidebar and Navbar
        window.location.reload();
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  // 1. Loading State (Spinner)
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-500 font-bold animate-pulse">Loading Profile...</p>
        </div>
      </div>
    );
  }

  // 2. Unauthorized State (e.g., if you are logged in as Admin but not User)
  if (!formData) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 text-center p-6 bg-gray-50">
        <ShieldAlert className="h-16 w-16 text-red-500" />
        <h2 className="text-2xl font-bold text-gray-800">User Session Required</h2>
        <p className="text-gray-600 max-w-md">
          We couldn't find a valid User session. Please log in as a User to manage your profile.
        </p>
        <button 
          onClick={() => { localStorage.clear(); window.location.href = "/login"; }}
          className="mt-2 px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition shadow-lg"
        >
          Go to User Login
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 shadow-sm">
            <User className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Account Settings</h2>
            <p className="text-xs sm:text-sm text-gray-500">Manage your profile & details</p>
          </div>
        </div>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="flex flex-1 md:flex-initial items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs sm:text-sm font-bold text-gray-600 transition hover:bg-gray-50 active:scale-95"
              >
                <X size={16} /> <span className="hidden sm:inline">Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex flex-1 md:flex-initial items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-xs sm:text-sm font-bold text-white transition hover:bg-blue-700 shadow-lg active:scale-95 disabled:opacity-70"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex w-full md:w-auto items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-xs sm:text-sm font-bold text-white transition hover:bg-blue-700 shadow-lg active:scale-95"
            >
              <Edit3 size={16} /> Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Main Form Card */}
      <div className="rounded-[1.5rem] border border-gray-100 bg-white shadow-xl shadow-gray-200/40 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 p-5 sm:p-6">
          
          {/* Full Name */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <User size={14} className="text-gray-400" /> Full Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name || ""}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-all ${
                isEditing
                  ? "border-blue-400 bg-white focus:ring-2 focus:ring-blue-50"
                  : "border-transparent bg-gray-50/50 text-gray-600 cursor-not-allowed"
              }`}
            />
          </div>
<div className="space-y-1.5">
  <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
    <Mail size={14} className="text-gray-400" /> Email Address
  </label>
  <input
    type="email"
    name="email"
    value={formData.email || ""}
    readOnly={true} // ⚡ Always true, regardless of isEditing state
    className="w-full rounded-xl border border-transparent bg-gray-50/50 px-4 py-2.5 text-sm text-gray-400 cursor-not-allowed outline-none"
    // Note: onChange is removed because this field shouldn't change
  />
  <p className="text-[10px] text-gray-400 mt-1 ml-1 italic">
    Email address cannot be changed.
  </p>
</div>

          {/* Company Name - Conditionally Rendered */}
          {(formData.userType === "Corporate Companies" || formData.role === "Corporate Companies") && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
                <Building2 size={14} className="text-gray-400" /> Company Name
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName || "N/A"}
                readOnly={!isEditing}
                onChange={handleInputChange}
                className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-all ${
                  isEditing
                    ? "border-blue-400 bg-white focus:ring-2 focus:ring-blue-50"
                    : "border-transparent bg-gray-50/50 text-gray-600 cursor-not-allowed"
                }`}
              />
            </div>
          )}

          {/* User Type */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <ShieldUser size={14} className="text-gray-400" /> User Type
            </label>
            <div className="w-full rounded-xl border border-transparent bg-gray-100/70 px-4 py-2.5 text-sm text-gray-500 font-medium italic">
              {formData.role || formData.userType || "User Account"}
            </div>
          </div>

          {/* Phone Number */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
              <Phone size={14} className="text-gray-400" /> Phone Number
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone || ""}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-all ${
                isEditing
                  ? "border-blue-400 bg-white focus:ring-2 focus:ring-blue-50"
                  : "border-transparent bg-gray-50/50 text-gray-600 cursor-not-allowed"
              }`}
            />
          </div>

          {/* Company Address - Conditionally Rendered */}
          {(formData.userType === "Corporate Companies" || formData.role === "Corporate Companies") && (
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
                <MapPin size={14} className="text-gray-400" /> Company Address
              </label>
              <input
                type="text"
                name="companyAddress"
                value={formData.companyAddress || "N/A"}
                readOnly={!isEditing}
                onChange={handleInputChange}
                className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition-all ${
                  isEditing
                    ? "border-blue-400 bg-white focus:ring-2 focus:ring-blue-50"
                    : "border-transparent bg-gray-50/50 text-gray-600 cursor-not-allowed"
                }`}
              />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-100 bg-gray-50/30 px-10 py-5">
          <p className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">
            Last Updated: {
              originalData?.updatedAt 
                ? new Date(originalData.updatedAt).toLocaleDateString('en-GB') 
                : "Just now" 
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfilesectionHome;